import type { LifecycleStateLabel } from "@/api/projects";
import { ACTIVE_RUN_STATES } from "@/api/runs";
import { STATE_LABEL } from "@/components/runs/run-state-label";

/**
 * **The lifecycle rail's model (VIS/V1).** Where a run in flight currently is,
 * derived from its lifecycle status and nothing else.
 *
 * ---
 *
 * ## The rail is a WALK of the state machine, not a list of state names
 *
 * The obvious implementation — iterate `ACTIVE_RUN_STATES` and mark everything
 * before the current status as done — is wrong, and wrong in the direction this
 * unit exists to avoid: it asserts a history the run does not have.
 *
 * `cached` is not a step on the way to `running`. The lifecycle
 * (`flowstack.lifecycle.state_machine._LEGAL_TRANSITIONS`) has exactly one
 * branch, and it is at the very first state:
 *
 *     validating ──▶ cached ─────────────────────────────────────▶ completed
 *          └──────▶ staging_inputs ──▶ dispatched ──▶ running
 *                        ──▶ collecting_outputs ──▶ validating_outputs ──▶ completed
 *
 * A run served from cache NEVER stages inputs, never dispatches and never runs;
 * a run that dispatched never passes through `cached`. So a single flat rail
 * over the seven active labels would tell a cached run that staging, dispatch
 * and execution are "still to come" — five stages that will not happen — and
 * would tell a running run it had already been "cached". Both are claims about
 * a run that the status contradicts.
 *
 * Hence {@link LIFECYCLE_PATHS}: two walks, and the run's status picks which
 * one it is on. Each is a legal walk of {@link LIFECYCLE_SUCCESSORS}, and
 * `run-lifecycle-rail.test.ts` proves it rather than trusting the ordering
 * above to have been typed correctly.
 *
 * ## While the run is still `validating`, the branch has NOT been taken
 *
 * The paragraph above says "the run's status picks which walk it is on", and
 * for six of the seven active states that is true. For the seventh it is not,
 * and the gap is the same class of overclaim this module exists to avoid
 * (raised as an advisory on #1222): `validating` is the state BEFORE the fork,
 * so it carries no information about which successor follows. Selecting the
 * dispatch walk there tells a run that is about to hit the cache that staging,
 * dispatch and execution are coming; selecting the cache walk makes the inverse
 * error for every run that actually dispatches.
 *
 * So a `validating` run gets a fourth mark. The two walks share exactly two
 * stages — `validating` and `completed` ({@link ON_EVERY_WALK}) — and those two
 * are on the run's route whichever way the fork goes, which is a weaker claim
 * than "certain": no stage is certain, since every state on the dispatch walk
 * also has a `failed_*` successor. Every stage between them is drawn
 * `undecided` rather than `ahead`: shown, so the reader still sees the shape of
 * the pipeline, but explicitly not yet a claim about THIS run. {@link
 * railCaption} names the other branch in words, because a dimmed stage on its
 * own does not say what the alternative is.
 *
 * ## Why `ACTIVE_RUN_STATES` is still the source of truth
 *
 * The dispatch's constraint — "do not hand-write a parallel list of state
 * names; if the rail and the polling gate can disagree, they eventually will" —
 * is discharged by {@link lifecycleRail} returning `null` for anything outside
 * `ACTIVE_RUN_STATES`, and by the test asserting that the two paths' non-
 * terminal stages are EXACTLY that set. Add an eighth active state to
 * `api/runs.ts` without placing it on a path and the suite goes red; the rail
 * cannot silently fall behind the thing that decides whether to keep polling.
 *
 * ## Terminal runs get no rail at all
 *
 * `lifecycleRail` is `null` for the five terminal labels, and that is a
 * decision about honesty rather than about scope.
 *
 * A settled run's status alone cannot reconstruct the path it took. `completed`
 * is reachable from `cached` AND from `validating_outputs`, so a completed run
 * rendered on the dispatch rail would claim it staged, dispatched and ran —
 * unknowable here, and false for every cache hit. The four `failed_*` labels
 * are worse: `failed_system` is reachable from `staging_inputs`, `dispatched`
 * OR `running`, so a rail would have to pick one of three stages to mark as
 * where the run died and would be wrong about two of them.
 *
 * The transition history that DOES answer this is real and already surfaced —
 * `GET /api/runs/{id}/logs` renders the append-only `run_transitions` audit
 * trail, which is the Logs tab. So the rail declines the question and the run
 * detail page points at the surface that can answer it.
 */

/**
 * Legal forward transitions, mirroring
 * `flowstack.lifecycle.state_machine._LEGAL_TRANSITIONS`.
 *
 * A mirror rather than an import, for the same reason `LifecycleStateLabel` in
 * `api/projects.ts` is one — the browser cannot read Python. What keeps the two
 * from drifting is `tests/lifecycle/test_lifecycle_rail_mirror.py`, which parses
 * THIS map out of THIS file and asserts it equals the state machine's, so the
 * copy is checked on every CI run rather than trusted.
 *
 * Only the forward edges matter here; the rail never renders a failure sink
 * (see the file docstring), but they are kept so the mirror test compares the
 * whole graph rather than a curated slice of it.
 */
export const LIFECYCLE_SUCCESSORS: Record<LifecycleStateLabel, readonly LifecycleStateLabel[]> = {
  validating: ["cached", "staging_inputs", "failed_validation"],
  cached: ["completed"],
  staging_inputs: ["dispatched", "failed_validation", "failed_system"],
  dispatched: ["running", "failed_system"],
  running: ["collecting_outputs", "failed_system", "failed_contract"],
  collecting_outputs: ["validating_outputs", "failed_contract"],
  validating_outputs: ["completed", "failed_scientific", "failed_contract"],
  completed: [],
  failed_validation: [],
  failed_system: [],
  failed_scientific: [],
  failed_contract: [],
};

/** Which of the state machine's two walks a run is on. */
export type RailPathKind = "dispatch" | "cache";

/**
 * The two walks from `validating` to `completed`, in lifecycle order.
 *
 * Both end at `completed` — a terminal label deliberately included as the
 * rail's last stage, because a rail whose final stop is `validating_outputs`
 * would not show the reader what finishing looks like. It is always marked
 * `ahead`: {@link lifecycleRail} never runs for a terminal status, so the run
 * has by construction not reached it.
 */
export const LIFECYCLE_PATHS: Record<RailPathKind, readonly LifecycleStateLabel[]> = {
  dispatch: [
    "validating",
    "staging_inputs",
    "dispatched",
    "running",
    "collecting_outputs",
    "validating_outputs",
    "completed",
  ],
  cache: ["validating", "cached", "completed"],
};

/** Every walk, in declaration order — the input the derivations below fold. */
const ALL_WALKS: readonly (readonly LifecycleStateLabel[])[] = Object.values(LIFECYCLE_PATHS);

/**
 * The walk DRAWN before the fork, and the one a status off every walk falls
 * back to: the longest.
 *
 * Longest because the rail's job before the fork is to show the reader the
 * SHAPE of the pipeline, and the longest walk is the only one that contains
 * every stage the others do (`cache` ⊄ `dispatch` is not true of the state
 * names, but `dispatch` is the superset the reader learns the most from). This
 * is a presentation choice, and the only one here that a third walk would make
 * genuinely arbitrary — everything else below folds over `ALL_WALKS`.
 */
const DRAWN_BEFORE_THE_FORK: RailPathKind = (Object.keys(LIFECYCLE_PATHS) as RailPathKind[]).reduce(
  (longest, kind) =>
    LIFECYCLE_PATHS[kind].length > LIFECYCLE_PATHS[longest].length ? kind : longest
);

/**
 * The stages that lie on EVERY walk, so the fork cannot route around them.
 *
 * **Not "certain"** (flowstack#1224 finding 1): being on every walk says a
 * stage cannot be SKIPPED, never that the run will reach it — every walk can
 * also end early at a terminal failure, which is why no failure label appears
 * on one. See {@link RailMark}.
 *
 * Folded over {@link ALL_WALKS} rather than intersecting the two by name
 * (flowstack#1224 finding 3): a third walk narrows this automatically, where
 * `dispatch.filter(s => cache.includes(s))` would have kept quietly answering
 * a two-walk question.
 */
export const ON_EVERY_WALK: ReadonlySet<LifecycleStateLabel> = new Set(
  ALL_WALKS.reduce((shared, walk) => shared.filter((state) => walk.includes(state)))
);

/**
 * The states from which the walk is not yet determined — the prefix EVERY walk
 * agrees on. Today: just `validating`.
 *
 * Computed as the longest common prefix over {@link ALL_WALKS}, because "which
 * states come before the fork" is a property of the paths and not a fact worth
 * restating — and, as above, one that must narrow rather than mislead when a
 * third walk arrives.
 */
export const BEFORE_THE_FORK: ReadonlySet<LifecycleStateLabel> = (() => {
  const shortest = Math.min(...ALL_WALKS.map((walk) => walk.length));
  const shared: LifecycleStateLabel[] = [];
  for (let i = 0; i < shortest; i++) {
    if (ALL_WALKS.some((walk) => walk[i] !== ALL_WALKS[0][i])) break;
    shared.push(ALL_WALKS[0][i]);
  }
  return new Set(shared);
})();

/**
 * A stage's relation to where the run is now.
 *
 * `passed` and `ahead` are both FACTS about a walk whose order the state
 * machine fixes, while `current` is the one the status states directly. Nothing
 * here is an estimate, which is why the rail carries no "percent complete" —
 * the walk's steps are not equal in duration and pretending otherwise would
 * turn six facts into one guess.
 *
 * **`ahead` is a POSITION, not a prediction** (flowstack#1224 finding 1). It
 * says: this stage lies on the walk this run is on, later than where the run
 * reports being. It does not say the run will get there — every state on the
 * dispatch walk has at least one `failed_*` successor
 * ({@link LIFECYCLE_SUCCESSORS}), so an active run can leave the walk at any
 * point without reaching the next stage, let alone `completed`. This module
 * previously documented `ahead` as the assertion "this run will reach here",
 * which is the same class of overclaim it exists to remove; the mark's drawing
 * (a hollow ring — an outline, not a filled promise) always matched the weaker
 * reading, and now the words do too.
 *
 * `undecided` is the fourth, and it is a claim the rail declines one step
 * earlier than that: the run is still before the fork, so nothing yet says this
 * stage is on the run's walk AT ALL. The two are a ladder — `undecided` doubts
 * the stage is on the route, `ahead` places it on the route and stays silent
 * about arrival — which is why the pre-fork state needs its own mark rather
 * than reusing the weaker-sounding one.
 */
export type RailMark = "passed" | "current" | "ahead" | "undecided";

export interface RailStage {
  state: LifecycleStateLabel;
  /** Display text, from the one spelling in `run-state-label.ts`. */
  label: string;
  mark: RailMark;
}

export interface LifecycleRail {
  /** Which walk the run is on — the caption says so, since it is not obvious
   * from the stages alone that the other five were skipped rather than
   * pending. Meaningful only once {@link branchTaken} is true; before that it
   * is the walk being SHOWN, not the walk the run is on. */
  path: RailPathKind;
  /** False while the run is still before the fork (`validating`), when the
   * stages between the shared endpoints are `undecided` rather than `ahead`. */
  branchTaken: boolean;
  stages: readonly RailStage[];
  /** Index of the `current` stage in {@link stages}. Always in range. */
  currentIndex: number;
}

/**
 * The rail for `status`, or `null` when the run is not in flight.
 *
 * `null` covers every label outside `ACTIVE_RUN_STATES` — the five terminal
 * ones today. Callers render nothing rather than an empty rail: see the file
 * docstring for why a settled run's path is not reconstructible from its
 * status.
 */
export function lifecycleRail(status: LifecycleStateLabel): LifecycleRail | null {
  if (!ACTIVE_RUN_STATES.has(status)) return null;
  const branchTaken = !BEFORE_THE_FORK.has(status);
  // AFTER the fork the status selects the walk: an active label that is not on
  // every walk sits on exactly one, which `run-lifecycle-rail.test.ts` asserts
  // rather than assumes. Before the fork there is nothing to select on, so the
  // rail draws {@link DRAWN_BEFORE_THE_FORK} and marks every stage the other
  // walks do not share `undecided`, with the caption naming the branches in
  // words.
  //
  // Found rather than spelled `status === "cached" ? "cache" : "dispatch"`
  // (flowstack#1224 finding 3), which was a two-walk answer wearing a general
  // shape: a third walk would have been silently folded into `dispatch`.
  const path: RailPathKind =
    (Object.keys(LIFECYCLE_PATHS) as RailPathKind[]).find(
      (kind) => !ON_EVERY_WALK.has(status) && LIFECYCLE_PATHS[kind].includes(status)
    ) ?? DRAWN_BEFORE_THE_FORK;
  const walk = LIFECYCLE_PATHS[path];
  const currentIndex = walk.indexOf(status);
  // Unreachable while the test below holds (every active label sits on exactly
  // one walk), and a thrown error would take the whole run page down over a
  // cosmetic rail. Degrade to "no rail" instead.
  if (currentIndex < 0) return null;
  return {
    path,
    branchTaken,
    currentIndex,
    stages: walk.map((state, index) => ({
      state,
      label: STATE_LABEL[state],
      mark:
        index < currentIndex
          ? "passed"
          : index === currentIndex
            ? "current"
            : branchTaken || ON_EVERY_WALK.has(state)
              ? "ahead"
              : "undecided",
    })),
  };
}

/**
 * What the rail's caption says about the walk this run is on.
 *
 * The cache walk needs the sentence: a reader who knows the pipeline will
 * notice `staging inputs`, `dispatched` and `running` are missing from the rail
 * and the honest reason is that they will never happen, not that the rail is
 * incomplete. The dispatch walk gets the shorter note pointing at the Logs tab,
 * which is where the actual per-transition timestamps live.
 *
 * Before the fork there is a third caption, and it carries the whole weight of
 * the `undecided` mark: a dimmed stage says "not asserted" but cannot say WHAT
 * the alternative is, so the sentence names the cache branch outright. Without
 * it the rail would still be quietly implying a dispatch this run may never do.
 *
 * **That third caption used to name the two walks as the OUTCOMES**
 * (flowstack#1224 finding 1) — *"it either hits the cache and completes from
 * there, or walks the dispatch path shown"* — which is a closed set the state
 * machine does not support: every state on the dispatch walk has a `failed_*`
 * successor, so a run can take that branch and never complete. The two walks
 * are the SUCCESSFUL routes, and *"which branch"* and *"whether it finishes"*
 * are separate questions; the caption now answers only the first, which is the
 * one the rail is drawing. This matters more here than the wording suggests:
 * VIS/V1's premise is that every figure says which register it is in, so a
 * caption asserting exhaustiveness is that unit's own defect reappearing
 * inside it.
 */
export function railCaption(path: RailPathKind, branchTaken = true): string {
  if (!branchTaken) {
    return "This run is still validating, so it has not taken the lifecycle's branch yet: its successful routes are a cache hit that completes from there, or the dispatch path shown — and either can instead end in a failure, which the rail does not draw. The dimmed stages are that path — the shape of the pipeline, not yet a claim about this run. Per-transition timestamps are on the Logs tab.";
  }
  return path === "cache"
    ? "This run was served from the cache, so it takes the lifecycle's cache branch — it never stages inputs, dispatches or runs. Per-transition timestamps are on the Logs tab."
    : "Stages the lifecycle puts in this order on the route this run is taking; the rail marks which one the run reports being in, not how long each takes or whether it reaches the next. Per-transition timestamps are on the Logs tab.";
}
